import React from 'react';
import PropTypes from 'prop-types';
import Button from './common/ui/Button';
import { getSortLabel } from '../constants/filterOptions';

const ActiveFilterChips = ({
  filters,
  initialSort,
  genreLookup,
  tagLookup,
  warningLookup,
  onRemoveFilter,
  onResetField,
}) => {
  const chips = [];

  // Genre includes
  (filters.includeGenreIds ?? []).forEach((id) => {
    const label = genreLookup?.get(String(id))?.name ?? `Genre ${id}`;
    chips.push({
      key: `gin-${id}`,
      label: `Include: ${label}`,
      onRemove: () => onRemoveFilter('includeGenreIds', id),
    });
  });

  // Genre excludes
  (filters.excludeGenreIds ?? []).forEach((id) => {
    const label = genreLookup?.get(String(id))?.name ?? `Genre ${id}`;
    chips.push({
      key: `gex-${id}`,
      label: `Exclude: ${label}`,
      onRemove: () => onRemoveFilter('excludeGenreIds', id),
    });
  });

  // Tag includes
  (filters.includeTagIds ?? []).forEach((id) => {
    const label = tagLookup?.get(String(id))?.name ?? `Tag ${id}`;
    chips.push({
      key: `tin-${id}`,
      label: `Include: ${label}`,
      onRemove: () => onRemoveFilter('includeTagIds', id),
    });
  });

  // Tag excludes
  (filters.excludeTagIds ?? []).forEach((id) => {
    const label = tagLookup?.get(String(id))?.name ?? `Tag ${id}`;
    chips.push({
      key: `tex-${id}`,
      label: `Exclude: ${label}`,
      onRemove: () => onRemoveFilter('excludeTagIds', id),
    });
  });

  // Content warning excludes
  (filters.excludeContentWarningIds ?? []).forEach((id) => {
    const label = warningLookup?.get(String(id))?.name ?? `Warning ${id}`;
    chips.push({
      key: `wex-${id}`,
      label: `No ${label}`,
      onRemove: () => onRemoveFilter('excludeContentWarningIds', id),
    });
  });

  // Mature-content hide toggle
  if (filters.hideMature) {
    chips.push({
      key: 'hideMature',
      label: 'Hide 18+',
      onRemove: () => onResetField('hideMature', false),
    });
  }

  // Status
  if (filters.isCompleted && filters.isCompleted !== 'any') {
    chips.push({
      key: 'status',
      label: `Status: ${filters.isCompleted === 'completed' ? 'Completed' : 'Ongoing'}`,
      onRemove: () => onResetField('isCompleted', 'any'),
    });
  }

  // Sort (only if non-default)
  if (filters.sort && filters.sort !== initialSort) {
    chips.push({
      key: 'sort',
      label: `Sort: ${getSortLabel(filters.sort)}`,
      onRemove: () => onResetField('sort', initialSort),
    });
  }

  // Origin country
  if (filters.originCountry && filters.originCountry !== '') {
    chips.push({
      key: 'originCountry',
      label: `Origin: ${filters.originCountry}`,
      onRemove: () => onResetField('originCountry', ''),
    });
  }

  // Numeric range filters
  const numericChips = [
    { field: 'minRating', prefix: '★ ≥' },
    { field: 'maxRating', prefix: '★ ≤' },
    { field: 'minRatingCount', prefix: 'Ratings ≥' },
    { field: 'maxRatingCount', prefix: 'Ratings ≤' },
    { field: 'minChapterCount', prefix: 'Chapters ≥' },
    { field: 'maxChapterCount', prefix: 'Chapters ≤' },
    { field: 'minViewCount', prefix: 'Views ≥' },
    { field: 'maxViewCount', prefix: 'Views ≤' },
  ];

  numericChips.forEach(({ field, prefix }) => {
    if (filters[field] !== '' && filters[field] != null) {
      chips.push({
        key: field,
        label: `${prefix} ${filters[field]}`,
        onRemove: () => onResetField(field, ''),
      });
    }
  });

  if (chips.length === 0) return null;

  return (
    <div className="search-active-chips">
      <ul className="library-filters__chips" aria-label="Active filters">
        {chips.map((chip) => (
          <li key={chip.key} className="library-filters__chip">
            <span>{chip.label}</span>
            <Button
              variant="ghost"
              className="library-filters__chip-remove"
              onClick={chip.onRemove}
              aria-label={`Remove ${chip.label}`}
              style={{ padding: '2px', minWidth: 'auto', height: 'auto', borderRadius: '50%' }}
            >
              ×
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
};

ActiveFilterChips.propTypes = {
  filters: PropTypes.object.isRequired,
  initialSort: PropTypes.string.isRequired,
  genreLookup: PropTypes.instanceOf(Map),
  tagLookup: PropTypes.instanceOf(Map),
  warningLookup: PropTypes.instanceOf(Map),
  onRemoveFilter: PropTypes.func.isRequired,
  onResetField: PropTypes.func.isRequired,
};

export default ActiveFilterChips;
